import React, { useEffect, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import API from '../../services/api';

const CloudinaryUploadWidget = ({ productId, onUploadSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [widget, setWidget] = useState(null);
  const [widgetReady, setWidgetReady] = useState(false);
  
  const initializeWidget = async () => {
    if (!window.cloudinary || !window.cloudinary.createUploadWidget) {
      // Auto-load the Cloudinary upload widget script if not present
      if (!document.querySelector('script[src*="widget.cloudinary.com"]')) {
        const script = document.createElement('script');
        script.src = 'https://widget.cloudinary.com/v2.0/global/all.js';
        script.async = true;
        document.body.appendChild(script);
      }
      setTimeout(initializeWidget, 500);
      return;
    }
    
    try {
      const { data: sig } = await API.post('/image/widget-signature/');
      
      // Detect if mobile
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      const cloudinaryWidget = window.cloudinary.createUploadWidget(
        {
          cloudName: sig.cloud_name,
          apiKey: sig.api_key,
          uploadSignature: sig.signature,
          uploadSignatureTimestamp: sig.timestamp,
          folder: sig.folder,
          
          // Use only 'local' source - it handles camera on mobile via native picker
          sources: ['local'],
          
          // This is KEY for mobile camera access
          // It adds capture attribute to file input, triggering camera on mobile
          fieldName: 'file',
          form: '#upload-form',
          
          multiple: true,
          maxFiles: 5,
          maxFileSize: 100000000, // 100MB
          clientAllowedFormats: ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'avif'],

          // Display settings
          cropping: false, // Skip cropping step - no full image preview
          thumbnailTransformation: [{ width: 120, height: 120, crop: 'fill' }],
          showUploadMoreButton: true,
          singleUploadAutoClose: false,

          // Auto-resize images on upload (before storing)
          // This limits max dimensions while preserving aspect ratio
          transformation: [
            { width: 1920, height: 1920, crop: 'limit', quality: 'auto:good' }
          ],
          
          // Mobile-specific: this makes the file picker show camera option
          ...(isMobile && {
            sources: ['local'],
            fieldName: 'file',
            secure: true,
          }),
          
          showAdvancedOptions: false,
          showCompletedButton: true,
          showPoweredBy: false,
          
          language: 'he',
          text: {
            he: {
              "or": "או",
              "back": "חזור",
              "close": "סגור",
              "upload": "העלה",
              "upload_more": "העלה עוד",
              "add_more": "הוסף עוד",
              "show_completed": "הצג שהושלמו",
              "done": "סיום",
              "action": {
                "upload": "העלה",
                "upload_more": "העלה עוד"
              },
              "actions": {
                "upload_more": "העלה עוד",
                "done": "סיום",
                "cancel": "ביטול"
              },
              "menu": {
                "files": "הקבצים שלי",
                "camera": "מצלמה",
                "web": "כתובת אינטרנט",
                "url": "כתובת אינטרנט"
              },
              "selection_counter": {
                "selected": "נבחרו"
              },
              "local": {
                "browse": "בחר קבצים",
                "dd_title_single": "גרור תמונה לכאן",
                "dd_title_multi": "גרור תמונות לכאן",
                "drop_title_single": "שחרר קובץ כאן",
                "drop_title_multiple": "שחרר קבצים כאן"
              },
              "camera": {
                "upload": "העלה",
                "capture": "צלם תמונה",
                "cancel": "ביטול",
                "take_pic": "צלם והעלה",
                "explanation": "וודא שהמצלמה מחוברת ושהדפדפן מאפשר גישה למצלמה. כשתהיה מוכן, לחץ על צלם.",
                "camera_error": "גישה למצלמה נכשלה",
                "retry": "נסה שוב"
              },
              "queue": {
                "title": "רשימת העלאה",
                "title_uploading_with_counter": "מעלה {{num}} קבצים",
                "title_processing_with_counter": "מעבד {{num}} קבצים", 
                "title_uploading": "מעלה קבצים",
                "title_completed": "העלאה הושלמה",
                "mini_title": "העלאות",
                "mini_title_uploading": "מעלה",
                "show_completed": "הצג שהושלמו",
                "retry_failed": "נסה שוב כושלים",
                "abort_all": "בטל הכל",
                "upload_more": "העלה עוד",
                "done": "סיום",
                "mini_upload_count": "{{num}} הועלו",
                "mini_failed": "{{num}} נכשלו",
                "statuses": {
                  "uploading": "מעלה...",
                  "error": "שגיאה",
                  "uploaded": "הועלה בהצלחה",
                  "aborted": "בוטל",
                  "processing": "מעבד...",
                  "processed": "הושלם"
                }
              },
              "uploader": {
                "filesize": {
                  "na": "לא זמין",
                  "b": "בתים",
                  "k": "ק\"ב",
                  "m": "מ\"ב",
                  "g": "ג\"ב",
                  "t": "ט\"ב"
                }
              },
              "crop": {
                "title": "חתוך תמונה",
                "crop_btn": "חתוך והעלה",
                "skip_btn": "דלג",
                "cancel_btn": "ביטול",
                "reset_btn": "איפוס"
              },
              "notifications": {
                "general_error": "אירעה שגיאה",
                "general_prompt": "האם אתה בטוח?",
                "limit_reached": "לא ניתן להעלות קבצים נוספים",
                "invalid_add_url": "חייב להיות URL תקין",
                "invalid_public_id": "לא ניתן להכיל 'api_key' או 'api_secret'"
              }
            }
          },
          
          styles: {
            palette: {
              window: "#FFFFFF",
              windowBorder: "#1976d2",
              tabIcon: "#1976d2",
              action: "#4caf50"
            }
          }
        },
        
        async (error, result) => {
          if (result.event === 'success') {
            setLoading(true);
            try {
              await API.post('/image/register/', {
                public_id: result.info.public_id,
                version: result.info.version,
                format: result.info.format,
                model_type: 'product',
                object_id: productId
              });
              if (onUploadSuccess) onUploadSuccess(result.info);
            } catch (err) {
              console.error('Registration failed:', err);
            }
            setLoading(false);
          }
        }
      );
      
      setWidget(cloudinaryWidget);
      setWidgetReady(true);
    } catch (error) {
      console.error('Widget initialization failed:', error);
      setTimeout(initializeWidget, 1000);
    }
  };
  
  useEffect(() => {
    initializeWidget();
  }, [productId]);
  
  const handleClick = () => {
    if (widget && widgetReady) {
      widget.open();
    } else {
      initializeWidget();
    }
  };
  
  return (
    <Button
      variant="contained"
      startIcon={loading ? <CircularProgress size={20} /> : <AddPhotoAlternateIcon />}
      onClick={handleClick}
      disabled={loading || !widgetReady}
      sx={{ direction: 'ltr' }}
    >
      {loading ? 'מעלה...' : !widgetReady ? 'טוען...' : 'העלה תמונות'}
    </Button>
  );
};

export default CloudinaryUploadWidget;